import { f7, useStore } from 'framework7-react';
import { Router } from 'framework7/types';
import { FC, useEffect, useState } from 'react';
import { AccountRegistPayment } from './AccountRegistPayment/AccountRegistPayment';
import { registerPaidyTokenApi } from '@/api/payment';
import { isApp } from '@/config/device';
import { paths } from '@/config/paths';
import { store } from '@/config/store';
import { useCustomerInfo } from '@/hooks/api/customer/useCustomerInfo';
import { useGmo } from '@/hooks/useGmo';
import { useGmoRegisterAndEditRedirect } from '@/hooks/useGmoRegisterAndEditRedirect';
import { useLoadCustomer } from '@/hooks/useLoadCustomer';
import { usePaidy } from '@/hooks/usePaidy';
import { Car } from '@/types/api/customerApi';
import { storeDispatch } from '@/utils/store';
import { f7CustomBack } from '@/utils/utils';

interface AccountRegistPaymentPageProps {
  f7router: Router.Router;
  status?: string;
  currentCarId: string;
  fromPage?: string;
}
export const AccountRegistPaymentPage: FC<AccountRegistPaymentPageProps> = ({
  f7router,
  status,
  currentCarId,
  fromPage = paths.account,
}) => {
  const router = f7.views.main.router;
  const { m_car_id, t_order_id } =
    (useStore(store, 'carItemInfo') as Car) || {};
  const [actionUrl, setActionUrl] = useState<string>('');
  const [currentStatus] = useState(status);
  const customerId = window.localStorage.getItem('m_customer_id');
  const token =
    window.localStorage.getItem('token') === null
      ? undefined
      : window.localStorage.getItem('token');

  const { isLoggedIn } = useLoadCustomer(
    router,
    token === null ? undefined : token,
    Number(customerId),
    currentStatus,
    !status && store.state.authInfo.isLogin,
  );

  const { handleGmoRegisterAndEditRedirect } = useGmoRegisterAndEditRedirect();

  // GMOセッション再利用検出（同期）: 初回レンダリングで間違ったページの描画を防ぐ。
  const [isGmoRedirecting] = useState(() => {
    const expectedReturn = window.localStorage.getItem('gmo_expected_return');
    const expectedCancel = window.localStorage.getItem('gmo_expected_cancel');
    if (!expectedReturn && !expectedCancel) return false;
    if (
      status === 'gmo_success' &&
      expectedReturn &&
      !expectedReturn.includes('/account_payment_regist/')
    )
      return true;
    if (
      !status &&
      expectedCancel &&
      !expectedCancel.includes('/account_payment_regist')
    )
      return true;
    return false;
  });

  // GMOセッション再利用検出: GMOが前回セッションのURLにリダイレクトした場合、
  // 期待する正しいページに転送する
  useEffect(() => {
    // PrepaidSettingsPageからのリダイレクト後、退避したfromPageを復元する。
    // afterGmoRedirectのcleanupがfromPageを消すが、退避キーは残る。
    const redirectedFromPage = window.localStorage.getItem(
      'gmo_redirect_fromPage',
    );
    if (redirectedFromPage) {
      window.localStorage.setItem('fromPage', redirectedFromPage);
      window.localStorage.removeItem('gmo_redirect_fromPage');
    }

    const expectedReturn = window.localStorage.getItem('gmo_expected_return');
    const expectedCancel = window.localStorage.getItem('gmo_expected_cancel');
    if (!expectedReturn && !expectedCancel) return;

    const redirectToCorrectPage = (targetUrl: string) => {
      const currentFromPage = window.localStorage.getItem('fromPage');
      if (currentFromPage) {
        window.localStorage.setItem('gmo_redirect_fromPage', currentFromPage);
      }
      window.localStorage.removeItem('gmo_expected_return');
      window.localStorage.removeItem('gmo_expected_cancel');
      window.location.replace(targetUrl);
    };

    if (
      status === 'gmo_success' &&
      expectedReturn &&
      !expectedReturn.includes('/account_payment_regist/')
    ) {
      redirectToCorrectPage(expectedReturn);
      return;
    }
    // AccountRegistPaymentのcancel URLにはstatusがないため、
    // !statusかつ期待URLが別ページを指す場合もリダイレクトする
    if (
      !status &&
      expectedCancel &&
      !expectedCancel.includes('/account_payment_regist')
    ) {
      redirectToCorrectPage(expectedCancel);
      return;
    }
    // 正しいページに到着した場合、期待URLをクリーンアップ
    window.localStorage.removeItem('gmo_expected_return');
    window.localStorage.removeItem('gmo_expected_cancel');
  }, []);

  const { addCreditCardPaymentMethod, GMOCardEditParameters } = useGmo(
    'account_payment_regist',
  );
  const { launchPaidyCheckout } = usePaidy();
  const { setCustomerInfo } = useCustomerInfo(
    {
      m_customer_id: store.state.authInfo.m_customer_id,
    },
    store.state.authInfo.isLogin && !status,
  );

  const handleClickPaidyPayment = async () => {
    try {
      const result = await launchPaidyCheckout({ mode: 'token' });
      window.localStorage.setItem('fromPage', paths.payments);
      if (result.success && result.tokenId) {
        f7.preloader.show();
        const customerInfo = store.state.customerInfo;
        const res = await registerPaidyTokenApi({
          m_customer_id: store.state.authInfo.m_customer_id,
          paidy_token_id: result.tokenId,
          customer_name: `${customerInfo?.family_name ?? ''} ${
            customerInfo?.first_name ?? ''
          }`.trim(),
          customer_email: customerInfo?.email ?? '',
          phone_number: customerInfo?.phone_no1 ?? '',
        });
        f7.preloader.hide();
        if (res.data.success) {
          setCustomerInfo();
          f7.dialog.alert('', 'あと払い（ペイディ）の登録が完了しました');
        } else {
          f7.dialog.alert('', 'あと払い（ペイディ）の登録に失敗しました');
        }
      }
    } catch (error) {
      console.error('Paidy registration error:', error);
      f7.preloader.hide();
      f7.dialog.alert('', 'エラーが発生しました');
    }
  };

  const handleClickBack = () => {
    const storedFromPage = window.localStorage.getItem('fromPage');
    if (storedFromPage) {
      window.localStorage.removeItem('fromPage');
      window.localStorage.removeItem('destination_page');
      window.localStorage.removeItem('destination_step');
      if (storedFromPage === paths.account) {
        // 戻り先がaccountの場合、直接遷移（push-reverseアニメーション付き）
        router.navigate(paths.account, { transition: 'push-reverse' });
      } else {
        // GMOリダイレクト後はブラウザ履歴にGMO URLが残っているため、
        // 遷移先以降のhandleClickBackでf7CustomBack()(=router.back()=
        // window.history.back())を使うとGMO URLに戻ってしまう。
        // postGmoNavフラグでnavigate()を使うよう指示する。
        window.localStorage.setItem('postGmoNav', 'true');
        router.navigate(storedFromPage, { transition: 'push-reverse' });
      }
      // navigate()後のF7履歴から古いエントリを全削除して循環を防ぐ。
      // F7はnavigate()後に非同期でlocalStorageに履歴を保存するため、
      // 遅延してsplice + localStorage同期を行う。
      setTimeout(() => {
        const history = f7.view.main.router.history;
        if (history.length > 1) {
          history.splice(0, history.length - 1);
        }
        window.localStorage.setItem(
          'f7router-view_main-history',
          JSON.stringify(history),
        );
      }, 0);
    } else if (f7.view.main.router.history.length > 1) {
      // 通常のナビゲーション: F7履歴が正常なのでf7CustomBackで戻る
      f7CustomBack();
    } else {
      // GMOリダイレクト後にfromPageが失われた場合のフォールバック
      router.navigate(paths.account, { transition: 'push-reverse' });
    }
  };

  const handleClickWithdrawFromBank = () => {
    const {
      globalTheme,
      isLoggedInToMaikuru,
      loginInfo: { loginType },
    } = store.state;
    window.localStorage.setItem('m_car_id', String(m_car_id));
    currentCarId && window.localStorage.setItem('currentCarId', currentCarId);
    window.localStorage.setItem('loginType', String(loginType));
    window.localStorage.setItem('loginTel', store.state.loginInfo?.tel || '');
    window.localStorage.setItem('t_order_id', String(t_order_id));
    window.localStorage.setItem('globalTheme', `${globalTheme}`);
    window.localStorage.setItem('fromPage', fromPage);
    window.localStorage.setItem(
      'isLoggedInToMaikuru',
      `${isLoggedInToMaikuru}`,
    );
    router.navigate(paths.registerBank, {
      props: {
        redirectPage: 'account_payment_regist',
      },
    });
  };

  const handleClickCreditCardPayment = async () => {
    const { token, m_customer_id } = store.state.authInfo;
    const {
      globalTheme,
      isLoggedInToMaikuru,
      loginInfo: { loginType },
    } = store.state;
    window.localStorage.setItem('token', token);
    window.localStorage.setItem('m_customer_id', m_customer_id.toString());
    window.localStorage.setItem('m_car_id', String(m_car_id));
    window.localStorage.setItem('loginType', String(loginType));
    window.localStorage.setItem('loginTel', store.state.loginInfo?.tel || '');
    window.localStorage.setItem('t_order_id', String(t_order_id));
    currentCarId && window.localStorage.setItem('currentCarId', currentCarId);
    window.localStorage.setItem('globalTheme', `${globalTheme}`);
    window.localStorage.setItem('fromPage', fromPage);
    window.localStorage.setItem(
      'isLoggedInToMaikuru',
      `${isLoggedInToMaikuru}`,
    );
    const returnUrl = paths.accountRegistPayment('gmo_success');
    const cancelUrl = paths.accountRegistPayment();
    try {
      const response = await addCreditCardPaymentMethod(returnUrl, cancelUrl); //GMOクレカ登録生成URL
      if (!response) {
        f7.dialog.alert('', `エラーが発生しました`);
        return;
      }
      if (
        response.error ||
        !response.success ||
        !('LinkUrl' in response.data)
      ) {
        f7.dialog.alert('', `エラーが発生しました`);
      } else {
        const GMOLinkTypePlusUrl = response.data.LinkUrl;
        // useGmoのaddCreditCardPaymentMethodがfromPageをreturnUrlで上書きするため、
        // 正しい遷移元パスを再設定する
        window.localStorage.setItem('fromPage', fromPage);
        if (isApp) {
          location.href = GMOLinkTypePlusUrl;
        } else {
          window.location.href = GMOLinkTypePlusUrl;
        }
      }
    } catch (error) {
      console.error('GMOパラメータの生成に失敗しました', error);
    } finally {
      // setIsProcessing(false);
    }
  };

  // GMOリダイレクト後はSPAリロードでストアが初期化されmenu='home'になるため、
  // マウント時に即座にタブバーを'account'に設定する。
  // このページは常にアカウント系フローからアクセスされるため無条件で安全。
  useEffect(() => {
    storeDispatch('setMenu', 'menu', 'account');
  }, []);

  useEffect(() => {
    if (isLoggedIn && currentStatus) {
      handleGmoRegisterAndEditRedirect(currentStatus, isLoggedIn);
      // GMOステータスURLをクリーンアップ（リロード時の再実行防止）
      const cleanUrl = paths.accountRegistPayment();
      window.history.replaceState({}, '', cleanUrl);
      const routerHistory = f7.view.main.router.history;
      if (routerHistory.length > 0) {
        routerHistory[routerHistory.length - 1] = cleanUrl;
      }
      // GMOリダイレクト後の不要なlocalStorageを削除
      window.localStorage.removeItem('destination_page');
      window.localStorage.removeItem('destination_step');
      window.localStorage.removeItem('source_page');
      // F7履歴をlocalStorageに同期（後続のGMOリダイレクト時に
      // 古い履歴が復元されるのを防ぐ）
      window.localStorage.setItem(
        'f7router-view_main-history',
        JSON.stringify(routerHistory),
      );
    }
  }, [isLoggedIn, currentStatus]);

  // GMOセッション再利用リダイレクト中は何も描画しない（フラッシュ防止）
  if (isGmoRedirecting) return null;

  return (
    <AccountRegistPayment
      status={status}
      GMOCardEditParameters={GMOCardEditParameters.data}
      handleClickBack={handleClickBack}
      handleClickCreditCardPayment={handleClickCreditCardPayment}
      handleClickWithdrawFromBank={handleClickWithdrawFromBank}
      handleClickPaidyPayment={handleClickPaidyPayment}
    />
  );
};
